import { useCallback, useEffect, useMemo, useRef, useState, type KeyboardEvent } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import ReactQuill from "react-quill";
import "react-quill/dist/quill.snow.css";
import {
  Mail,
  ExternalLink,
  Plus,
  X,
  ChevronDown,
  Paperclip,
  Trash2,
} from "lucide-react";
import { toast } from "sonner";
import { fetchMyDeskMailbox } from "services/desk.service";
import {
  fetchProjectDeskEmails,
  sendProjectDeskEmail,
  ProjectDeskEmailThread,
} from "services/project-desk-emails.service";
import { DeskMailboxConfig } from "types/Desk";
import { formatPrettyDate } from "utils/helperFunctions";

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const MAX_ATTACHMENTS = 10;
const MAX_ATTACHMENT_BYTES = 12 * 1024 * 1024;

const QUILL_MODULES = {
  toolbar: [
    ["bold", "italic", "underline", "strike"],
    [{ color: [] }, { background: [] }],
    [{ list: "ordered" }, { list: "bullet" }],
    [{ indent: "-1" }, { indent: "+1" }],
    ["blockquote", "link"],
    ["clean"],
  ],
};

function splitEmails(value: string): string[] {
  return value
    .split(/[,;\s]+/)
    .map((s) => s.trim())
    .filter(Boolean);
}

function formatThreadWhen(ts: number | null): string {
  if (!ts) return "—";
  return formatPrettyDate(ts, "lt-LT", true);
}

function htmlToPlainText(html: string): string {
  if (!html) return "";
  const div = document.createElement("div");
  div.innerHTML = html;
  return (div.textContent || div.innerText || "")
    .replace(/\u00a0/g, " ")
    .replace(/\n{3,}/g, "\n\n")
    .trim();
}

function isEmptyHtml(html: string): boolean {
  return !htmlToPlainText(html);
}

function formatFileSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

type ChipFieldProps = {
  label: string;
  emails: string[];
  onChange: (emails: string[]) => void;
  placeholder?: string;
  onClearAll?: () => void;
  showClearAll?: boolean;
};

function EmailChipField({
  label,
  emails,
  onChange,
  placeholder,
  onClearAll,
  showClearAll,
}: ChipFieldProps) {
  const [draft, setDraft] = useState("");
  const inputRef = useRef<HTMLInputElement>(null);

  const commitDraft = () => {
    const parts = splitEmails(draft);
    if (!parts.length) {
      setDraft("");
      return;
    }
    const next = [...emails];
    parts.forEach((p) => {
      if (!next.includes(p)) next.push(p);
    });
    onChange(next);
    setDraft("");
  };

  const onKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter" || e.key === "," || e.key === "Tab") {
      if (draft.trim()) {
        e.preventDefault();
        commitDraft();
      }
    }
    if (e.key === "Backspace" && !draft && emails.length) {
      onChange(emails.slice(0, -1));
    }
  };

  const removeAt = (index: number) => {
    onChange(emails.filter((_, i) => i !== index));
  };

  return (
    <div className="group flex min-h-[2.75rem] items-start gap-3 border-b border-zinc-200 px-4 py-2 dark:border-zinc-700">
      <span className="mt-1.5 w-14 shrink-0 text-sm text-zinc-500">{label}</span>
      <div
        className="flex min-w-0 flex-1 cursor-text flex-wrap items-center gap-1.5"
        onClick={() => inputRef.current?.focus()}
      >
        {emails.map((email, index) => (
          <span
            key={`${email}-${index}`}
            className="inline-flex max-w-full items-center gap-1 rounded-full bg-zinc-100 py-0.5 pl-2.5 pr-1 text-sm text-zinc-800 dark:bg-zinc-800 dark:text-zinc-100"
          >
            <span className="truncate">{email}</span>
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                removeAt(index);
              }}
              className="flex h-5 w-5 items-center justify-center rounded-full text-zinc-500 hover:bg-zinc-200 hover:text-zinc-800 dark:hover:bg-zinc-700"
              aria-label="Remove"
            >
              <X className="h-3 w-3" />
            </button>
          </span>
        ))}
        <input
          ref={inputRef}
          type="text"
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          onBlur={commitDraft}
          onKeyDown={onKeyDown}
          placeholder={emails.length ? "" : placeholder}
          className="min-w-[8rem] flex-1 border-0 bg-transparent py-1 text-base text-zinc-900 outline-none placeholder:text-zinc-400 lg:text-sm dark:text-zinc-100"
        />
      </div>
      {showClearAll && emails.length > 0 && onClearAll && (
        <button
          type="button"
          onClick={onClearAll}
          className="mt-1 shrink-0 rounded p-1 text-zinc-400 opacity-0 hover:bg-zinc-100 hover:text-zinc-600 group-hover:opacity-100 [@media(hover:none)]:p-3.5 [@media(hover:none)]:opacity-100 dark:hover:bg-zinc-800"
          aria-label="Clear"
        >
          <X className="h-4 w-4" />
        </button>
      )}
    </div>
  );
}

type Props = {
  companyId: string;
  projectId: string;
  contactEmail?: string | null;
  contactName?: string | null;
  cardName?: string;
};

export default function ProjectEmailPanel({
  companyId,
  projectId,
  contactEmail,
  contactName,
  cardName,
}: Props) {
  const { t } = useTranslation();
  const [mailboxes, setMailboxes] = useState<DeskMailboxConfig[]>([]);
  const [mailboxId, setMailboxId] = useState<string>("");
  const [mailboxLoading, setMailboxLoading] = useState(true);
  const [threads, setThreads] = useState<ProjectDeskEmailThread[]>([]);
  const [threadsLoading, setThreadsLoading] = useState(true);
  const [replyThread, setReplyThread] = useState<ProjectDeskEmailThread | null>(
    null
  );
  const [toEmails, setToEmails] = useState<string[]>(() =>
    contactEmail && EMAIL_RE.test(contactEmail) ? [contactEmail] : []
  );
  const [ccEmails, setCcEmails] = useState<string[]>([]);
  const [bccEmails, setBccEmails] = useState<string[]>([]);
  const [showBcc, setShowBcc] = useState(false);
  const [subject, setSubject] = useState(() =>
    cardName ? `Re: ${cardName}` : ""
  );
  const [bodyHtml, setBodyHtml] = useState("");
  const [files, setFiles] = useState<File[]>([]);
  const [sending, setSending] = useState(false);
  const [recipientError, setRecipientError] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const loadMailboxes = useCallback(async () => {
    setMailboxLoading(true);
    try {
      const data = await fetchMyDeskMailbox(companyId);
      const list = data.mailboxes.filter((m) => m.isActive && m.hasCredentials);
      setMailboxes(list);
      setMailboxId((prev) =>
        prev && list.some((m) => m.id === prev) ? prev : list[0]?.id || ""
      );
    } catch {
      setMailboxes([]);
    } finally {
      setMailboxLoading(false);
    }
  }, [companyId]);

  const loadThreads = useCallback(async () => {
    setThreadsLoading(true);
    try {
      const data = await fetchProjectDeskEmails(companyId, projectId);
      setThreads(Array.isArray(data.threads) ? data.threads : []);
    } catch {
      toast.error(
        t("project_email_load_failed", {
          defaultValue: "Could not load email threads.",
        })
      );
    } finally {
      setThreadsLoading(false);
    }
  }, [companyId, projectId, t]);

  useEffect(() => {
    loadMailboxes();
    loadThreads();
  }, [loadMailboxes, loadThreads]);

  useEffect(() => {
    if (
      !replyThread &&
      contactEmail &&
      EMAIL_RE.test(contactEmail) &&
      toEmails.length === 0
    ) {
      setToEmails([contactEmail]);
    }
  }, [contactEmail, replyThread, toEmails.length]);

  const selectedMailbox = useMemo(
    () => mailboxes.find((m) => m.id === mailboxId) || null,
    [mailboxes, mailboxId]
  );

  const validateRecipients = useCallback((): boolean => {
    if (!toEmails.length) {
      setRecipientError(
        t("project_email_to_required", {
          defaultValue: "Add at least one recipient.",
        })
      );
      return false;
    }
    const all = [...toEmails, ...ccEmails, ...bccEmails];
    if (all.some((e) => !EMAIL_RE.test(e))) {
      setRecipientError(
        t("project_email_invalid_recipient", {
          defaultValue: "Enter valid email addresses.",
        })
      );
      return false;
    }
    setRecipientError(null);
    return true;
  }, [toEmails, ccEmails, bccEmails, t]);

  const startNewEmail = () => {
    setReplyThread(null);
    setToEmails(
      contactEmail && EMAIL_RE.test(contactEmail) ? [contactEmail] : []
    );
    setCcEmails([]);
    setBccEmails([]);
    setShowBcc(false);
    setSubject(cardName ? `Re: ${cardName}` : "");
    setBodyHtml("");
    setFiles([]);
    setRecipientError(null);
  };

  const startReply = (thread: ProjectDeskEmailThread) => {
    setReplyThread(thread);
    setToEmails(
      contactEmail && EMAIL_RE.test(contactEmail) ? [contactEmail] : []
    );
    setCcEmails([]);
    setBccEmails([]);
    setShowBcc(false);
    setSubject("");
    setBodyHtml("");
    setFiles([]);
    setRecipientError(null);
  };

  const addFiles = (list: FileList | null) => {
    if (!list || !list.length) return;
    const incoming = Array.from(list);
    const next = [...files];
    for (const file of incoming) {
      if (file.size > MAX_ATTACHMENT_BYTES) {
        toast.error(
          t("project_email_attachment_too_large", {
            defaultValue: "{{name}} is too large (max 12 MB).",
            name: file.name,
          })
        );
        continue;
      }
      if (next.length >= MAX_ATTACHMENTS) {
        toast.error(
          t("project_email_too_many_attachments", {
            defaultValue: "You can attach up to {{max}} files.",
            max: MAX_ATTACHMENTS,
          })
        );
        break;
      }
      const duplicate = next.some(
        (f) => f.name === file.name && f.size === file.size
      );
      if (!duplicate) next.push(file);
    }
    setFiles(next);
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  const removeFile = (index: number) => {
    setFiles((prev) => prev.filter((_, i) => i !== index));
  };

  const handleSend = async () => {
    if (!validateRecipients()) return;
    const text = htmlToPlainText(bodyHtml);
    if (!text) {
      toast.error(
        t("project_email_body_required", {
          defaultValue: "Message body is required.",
        })
      );
      return;
    }
    if (!replyThread && !subject.trim()) {
      toast.error(
        t("project_email_subject_required", {
          defaultValue: "Subject is required.",
        })
      );
      return;
    }
    if (!mailboxId) {
      toast.error(
        t("project_email_no_mailbox", {
          defaultValue: "Connect a mailbox in Desk settings first.",
        })
      );
      return;
    }

    setSending(true);
    try {
      await sendProjectDeskEmail(companyId, projectId, {
        to: toEmails.join(", "),
        cc: ccEmails.length ? ccEmails.join(", ") : undefined,
        bcc: bccEmails.length ? bccEmails.join(", ") : undefined,
        subject: replyThread ? undefined : subject.trim(),
        text,
        html: bodyHtml,
        mailboxId,
        cardNumber: replyThread?.number,
        contactName: contactName || undefined,
        files,
      });
      toast.success(t("project_email_sent", { defaultValue: "Email sent." }));
      setBodyHtml("");
      setFiles([]);
      if (!replyThread) {
        setSubject(cardName ? `Re: ${cardName}` : "");
      }
      await loadThreads();
    } catch (e: any) {
      const msg =
        e?.response?.data?.message ||
        t("project_email_send_failed", {
          defaultValue: "Could not send email.",
        });
      toast.error(msg);
    } finally {
      setSending(false);
    }
  };

  if (mailboxLoading) {
    return (
      <p className="py-6 text-center text-sm text-zinc-500">
        {t("loading", { defaultValue: "Loading…" })}
      </p>
    );
  }

  if (!mailboxes.length) {
    return (
      <div className="rounded-lg border border-dashed border-zinc-300 bg-zinc-50 p-8 text-center dark:border-zinc-700 dark:bg-zinc-900/40">
        <Mail className="mx-auto mb-3 h-8 w-8 text-zinc-400" />
        <p className="mb-2 text-sm font-medium text-zinc-900 dark:text-zinc-100">
          {t("project_email_connect_mailbox_title", {
            defaultValue: "Connect your mailbox to send email",
          })}
        </p>
        <p className="mb-4 text-sm text-zinc-500">
          {t("project_email_connect_mailbox_hint", {
            defaultValue:
              "Emails are sent through the Desk module using your connected mailbox.",
          })}
        </p>
        <Link
          to={`/app/${companyId}/desk/settings`}
          className="inline-flex items-center gap-1.5 rounded-md bg-indigo-600 px-4 py-2 text-sm font-semibold text-white hover:bg-indigo-500"
        >
          {t("project_email_open_desk_settings", {
            defaultValue: "Open Desk settings",
          })}
          <ExternalLink className="h-4 w-4" />
        </Link>
      </div>
    );
  }

  const subjectValue = replyThread
    ? replyThread.emailSubject || replyThread.title || ""
    : subject;

  return (
    <div className="space-y-6">
      <div className="overflow-hidden rounded-lg border border-zinc-200 bg-white shadow-sm dark:border-zinc-700 dark:bg-zinc-950">
        {/* Header: From */}
        <div className="flex min-h-[2.75rem] items-center gap-3 border-b border-zinc-200 px-4 py-2 dark:border-zinc-700">
          <span className="w-14 shrink-0 text-sm text-zinc-500">
            {t("project_email_from", { defaultValue: "From" })}
          </span>
          {mailboxes.length === 1 ? (
            <span className="truncate text-sm text-zinc-900 dark:text-zinc-100">
              {selectedMailbox?.address}
            </span>
          ) : (
            <div className="relative min-w-0 flex-1">
              <select
                value={mailboxId}
                onChange={(e) => setMailboxId(e.target.value)}
                className="w-full appearance-none truncate border-0 bg-transparent py-1 pr-6 text-base text-zinc-900 outline-none lg:text-sm dark:text-zinc-100"
              >
                {mailboxes.map((m) => (
                  <option key={m.id} value={m.id}>
                    {m.address}
                  </option>
                ))}
              </select>
              <ChevronDown className="pointer-events-none absolute right-0 top-1/2 h-4 w-4 -translate-y-1/2 text-zinc-400" />
            </div>
          )}
          <div className="ml-auto flex items-center gap-2">
            {replyThread && (
              <button
                type="button"
                onClick={startNewEmail}
                className="text-xs font-medium text-indigo-600 hover:underline"
              >
                {t("project_email_new", { defaultValue: "New email" })}
              </button>
            )}
            {!showBcc && (
              <button
                type="button"
                onClick={() => setShowBcc(true)}
                className="text-xs font-medium text-zinc-500 hover:text-zinc-800"
              >
                Bcc
              </button>
            )}
          </div>
        </div>

        <EmailChipField
          label={t("project_email_to", { defaultValue: "To" })}
          emails={toEmails}
          onChange={(emails) => {
            setToEmails(emails);
            setRecipientError(null);
          }}
          placeholder="name@example.com"
          showClearAll
          onClearAll={() => setToEmails([])}
        />

        <EmailChipField
          label={t("project_email_cc", { defaultValue: "Cc" })}
          emails={ccEmails}
          onChange={(emails) => {
            setCcEmails(emails);
            setRecipientError(null);
          }}
        />

        {showBcc && (
          <EmailChipField
            label={t("project_email_bcc", { defaultValue: "Bcc" })}
            emails={bccEmails}
            onChange={(emails) => {
              setBccEmails(emails);
              setRecipientError(null);
            }}
          />
        )}

        <div className="flex min-h-[2.75rem] items-center gap-3 border-b border-zinc-200 px-4 py-2 dark:border-zinc-700">
          <span className="w-14 shrink-0 text-sm text-zinc-500">
            {t("project_email_subject", { defaultValue: "Subject" })}
          </span>
          {replyThread ? (
            <span className="truncate text-sm text-zinc-700 dark:text-zinc-300">
              {subjectValue}
            </span>
          ) : (
            <input
              type="text"
              value={subject}
              onChange={(e) => setSubject(e.target.value)}
              placeholder={cardName || ""}
              className="min-w-0 flex-1 border-0 bg-transparent text-base text-zinc-900 outline-none placeholder:text-zinc-400 lg:text-sm dark:text-zinc-100"
            />
          )}
        </div>

        {/* Editor */}
        <div className="project-email-quill min-h-[14rem]">
          <ReactQuill
            theme="snow"
            value={bodyHtml}
            onChange={setBodyHtml}
            modules={QUILL_MODULES}
            placeholder={t("project_email_body_placeholder", {
              defaultValue: "Write your message…",
            })}
          />
        </div>

        {files.length > 0 && (
          <div className="flex flex-wrap gap-2 border-t border-zinc-200 px-4 py-2.5 dark:border-zinc-700">
            {files.map((file, index) => (
              <span
                key={`${file.name}-${file.size}-${index}`}
                className="inline-flex max-w-full items-center gap-1.5 rounded-md border border-zinc-200 bg-zinc-50 py-1 pl-2.5 pr-1 text-xs text-zinc-700 dark:border-zinc-700 dark:bg-zinc-900 dark:text-zinc-200"
              >
                <Paperclip className="h-3 w-3 shrink-0 text-zinc-400" />
                <span className="truncate">{file.name}</span>
                <span className="shrink-0 text-zinc-400">
                  {formatFileSize(file.size)}
                </span>
                <button
                  type="button"
                  onClick={() => removeFile(index)}
                  className="flex h-5 w-5 items-center justify-center rounded text-zinc-400 hover:bg-zinc-200 hover:text-zinc-700 dark:hover:bg-zinc-800"
                  aria-label={t("remove", { defaultValue: "Remove" })}
                >
                  <X className="h-3 w-3" />
                </button>
              </span>
            ))}
          </div>
        )}

        {recipientError && (
          <p className="border-t border-zinc-200 px-4 py-2 text-sm text-red-600 dark:border-zinc-700">
            {recipientError}
          </p>
        )}

        {/* Footer actions */}
        <div className="flex flex-wrap items-center justify-between gap-3 border-t border-zinc-200 bg-zinc-50/80 px-3 py-2.5 dark:border-zinc-700 dark:bg-zinc-900/50">
          <div className="flex items-center gap-1">
            <input
              ref={fileInputRef}
              type="file"
              multiple
              className="hidden"
              onChange={(e) => addFiles(e.target.files)}
            />
            <button
              type="button"
              disabled={sending || files.length >= MAX_ATTACHMENTS}
              onClick={() => fileInputRef.current?.click()}
              className="flex h-8 items-center gap-1.5 rounded-md px-2 text-sm text-zinc-600 hover:bg-zinc-200/80 hover:text-zinc-900 disabled:opacity-40 max-md:h-11 max-md:px-3 dark:text-zinc-300 dark:hover:bg-zinc-800"
              title={t("project_email_attach", { defaultValue: "Attachment" })}
            >
              <Paperclip className="h-4 w-4" />
              <span className="hidden sm:inline">
                {t("project_email_attach", { defaultValue: "Attach" })}
              </span>
            </button>
          </div>

          <div className="flex items-center gap-1.5">
            <button
              type="button"
              onClick={() => {
                setBodyHtml("");
                setFiles([]);
                setRecipientError(null);
              }}
              className="flex h-8 w-8 items-center justify-center rounded-md text-zinc-500 hover:bg-red-50 hover:text-red-600 max-md:h-11 max-md:w-11 dark:hover:bg-red-950/30"
              title={t("project_email_discard", { defaultValue: "Discard" })}
            >
              <Trash2 className="h-4 w-4" />
            </button>
            <button
              type="button"
              disabled={sending || isEmptyHtml(bodyHtml)}
              onClick={handleSend}
              className="rounded-md bg-emerald-600 px-4 py-1.5 text-sm font-semibold text-white hover:bg-emerald-500 disabled:opacity-40 max-md:min-h-11"
            >
              {sending
                ? t("project_email_sending", { defaultValue: "Sending…" })
                : t("project_email_send", { defaultValue: "Send" })}
            </button>
          </div>
        </div>
      </div>

      {/* Thread history */}
      <div>
        <div className="mb-2 flex items-center justify-between">
          <h3 className="text-sm font-semibold text-zinc-900 dark:text-zinc-100">
            {t("project_email_threads", { defaultValue: "Email history" })}
          </h3>
          <button
            type="button"
            onClick={startNewEmail}
            title={t("project_email_new", { defaultValue: "New email" })}
            className="rounded p-1 text-zinc-500 hover:bg-zinc-100 dark:hover:bg-zinc-800"
          >
            <Plus className="h-4 w-4" />
          </button>
        </div>

        {threadsLoading ? (
          <p className="text-sm text-zinc-500">
            {t("loading", { defaultValue: "Loading…" })}
          </p>
        ) : threads.length === 0 ? (
          <p className="text-sm text-zinc-500">
            {t("project_email_no_threads", {
              defaultValue: "No emails linked to this deal yet.",
            })}
          </p>
        ) : (
          <ul className="divide-y divide-zinc-100 overflow-hidden rounded-lg border border-zinc-200 dark:divide-zinc-800 dark:border-zinc-700">
            {threads.map((thread) => {
              const active = replyThread?.id === thread.id;
              return (
                <li key={thread.id}>
                  <button
                    type="button"
                    onClick={() => startReply(thread)}
                    className={
                      "flex w-full items-start gap-3 px-3 py-2.5 text-left transition-colors " +
                      (active
                        ? "bg-sky-50 dark:bg-sky-950/30"
                        : "bg-white hover:bg-zinc-50 dark:bg-zinc-950 dark:hover:bg-zinc-900")
                    }
                  >
                    <Mail className="mt-0.5 h-4 w-4 shrink-0 text-zinc-400" />
                    <div className="min-w-0 flex-1">
                      <div className="truncate text-sm font-medium text-zinc-900 dark:text-zinc-100">
                        {thread.emailSubject || thread.title}
                      </div>
                      <div className="mt-0.5 truncate text-xs text-zinc-500">
                        {thread.preview || "—"}
                      </div>
                      <div className="mt-1 flex items-center justify-between gap-2 text-[11px] text-zinc-400">
                        <span>{formatThreadWhen(thread.lastMessageAt)}</span>
                        <Link
                          to={`/app/${companyId}/desk/inbox?card=${thread.number}`}
                          onClick={(e) => e.stopPropagation()}
                          className="inline-flex items-center gap-0.5 hover:text-indigo-600"
                        >
                          #{thread.number}
                          <ExternalLink className="h-3 w-3" />
                        </Link>
                      </div>
                    </div>
                  </button>
                </li>
              );
            })}
          </ul>
        )}
      </div>

      <style>{`
        .project-email-quill .quill {
          display: flex;
          flex-direction: column;
        }
        .project-email-quill .ql-toolbar.ql-snow {
          border: none;
          border-bottom: 1px solid rgb(228 228 231);
          background: rgb(250 250 250);
          padding: 6px 8px;
        }
        .dark .project-email-quill .ql-toolbar.ql-snow {
          border-bottom-color: rgb(63 63 70);
          background: rgb(24 24 27);
        }
        .project-email-quill .ql-container.ql-snow {
          border: none;
          font-size: 14px;
          min-height: 180px;
        }
        /* 16px keeps iOS from zooming the page when the editor is focused. */
        @media (max-width: 1023.98px) {
          .project-email-quill .ql-container.ql-snow {
            font-size: 16px;
          }
        }
        .project-email-quill .ql-editor {
          min-height: 180px;
          padding: 16px;
        }
        .project-email-quill .ql-editor.ql-blank::before {
          font-style: normal;
          color: rgb(161 161 170);
          left: 16px;
        }
      `}</style>
    </div>
  );
}
